/**
 * OVERVOLT interactive tutorial — Sprint 73 (tutorial-impl-s73).
 *
 * Owns the 12-step opt-in tour the player launches from the title-screen
 * "Start Tutorial" button. See `docs/TUTORIAL_DESIGN.md` §3 for the canonical
 * step list, §4 for the overlay UI pattern, and §5 for the lifecycle.
 *
 * Determinism contract (§4.5): every visible string flows through the i18n
 * catalogue under the `tutorial.*` namespace; every sim mutation rides the
 * **existing** `__GAME__` test API hooks (`reset`, `setMode`,
 * `setDifficulty`, `placeTower`, `startWave`, `setOverload`). No new sim
 * primitives. The tour is paint + state-machine, never a simulation source —
 * a run started after the tour ends is byte-identical to a fresh title-
 * screen wave-0 build phase. Onboarding-disabled runs (Playwright / balance
 * harness via `bootGame`) never launch the tour, so the determinism contract
 * is intact.
 *
 * UI pattern (§4.1):
 *   - A semi-transparent dim layer over the whole canvas.
 *   - A neon ring + arrow highlight on the current step's target (a HUD chip
 *     or playfield tile, looked up per-step).
 *   - A movable neon text box carrying: eyebrow (STEP n/12 · title), body,
 *     and a Back / Skip / Next button row.
 *
 * Skipping always restores state to wave-0 build phase via `Game.reset()`
 * so the worst-case escape leaves a clean board.
 */
import { CONFIG } from '../config';
import { roundRect, withAlpha } from '../engine/util';
import type { TutorialStepIdKey } from '../i18n/en';
import { t } from '../i18n';
import { saveProfile, type Profile } from '../game/profile';
import type { GameState } from '../game/state';

/** Re-export so callers can name the step ids without dipping into i18n. */
export type TutorialStepId = TutorialStepIdKey;

/** Number of steps in the tour — kept aligned with `docs/TUTORIAL_DESIGN.md` §3. */
export const TUTORIAL_TOTAL_STEPS = 12;

/**
 * Canonical step order (id only) — matches the rows of the design doc §3
 * table. The body / title copy is resolved live via
 * `t('tutorial.step.<id>.{title,body}')`. The renderer indexes this array
 * by `stepIndex` (0..11) and the i18n catalogue carries the `tutorial.step`
 * keys in the same shape — see `src/i18n/en.ts`.
 */
export const TUTORIAL_STEP_IDS: readonly TutorialStepId[] = [
  'welcome',
  'path',
  'firstPlace',
  'wave1',
  'towerRoles',
  'powerGrid',
  'overloadDial',
  'overclock',
  'abilities',
  'mazePreview',
  'puzzlePreview',
  'wrapUp',
] as const;

const C = CONFIG.colors;
const FONT_DISPLAY = '"Chakra Petch", "Rajdhani", system-ui, sans-serif';
const FONT_MONO = '"Share Tech Mono", ui-monospace, "DejaVu Sans Mono", monospace';
const CW = CONFIG.canvas.width;
const CH = CONFIG.canvas.height;

/** Geometry of the tour's text-box card and its action buttons. */
const PANEL_W = 360;
const PANEL_H = 168;
/**
 * Default panel position — anchored bottom-centre of the canvas so the top-
 * of-screen HUD (READOUT BAR + COMMAND DOCK) stays visible. Individual steps
 * may temporarily move the panel; the dragging-on-a-timer affordance is
 * deferred per §4.1's "ship simple, only matters on steps 6 / 8" note —
 * the default position is good enough for every step we ship.
 */
const PANEL_DEFAULT_X = (CW - PANEL_W) / 2;
const PANEL_DEFAULT_Y = CH - PANEL_H - 32;

/** Action-button rects derived from the panel position. */
function buttonRects(panelX: number, panelY: number): {
  back: Rect;
  skip: Rect;
  next: Rect;
} {
  const rowY = panelY + PANEL_H - 38;
  const w = 100;
  const h = 26;
  const gap = 8;
  const totalW = w * 3 + gap * 2;
  const startX = panelX + (PANEL_W - totalW) / 2;
  return {
    back: { x: startX, y: rowY, w, h },
    skip: { x: startX + (w + gap), y: rowY, w, h },
    next: { x: startX + (w + gap) * 2, y: rowY, w, h },
  };
}

/** A plain clickable rectangle, mirrors `hud.ts:Rect`. */
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Minimal seam exposed by `Game` to the tour, so this module never imports
 * the Game class directly (which would create a circular dep: Game already
 * imports onboarding + hud, which forward into here).
 */
export interface TutorialHost {
  /** Snapshot of the current state — the tour reads it, never writes. */
  getState(): GameState;
  /** Reset the run to a fresh wave-0 build phase on the current seed / mode. */
  resetRun(): void;
  /**
   * Persist the latest `seenFullTutorial` flag — the tour owns the flag's
   * lifecycle (sets on completion / skip), the host owns the actual
   * `localStorage` write through its already-loaded profile name.
   */
  persistProfile(): void;
}

/**
 * Owns the tutorial state + render layer. One instance lives on `Game`
 * alongside the existing `Onboarding`. While `active === true` the tour
 * captures pointer + Esc input and paints the overlay; otherwise it is
 * a no-op.
 */
export class Tutorial {
  /** True while the tour is running (between `start()` and `exit()`). */
  private _active = false;
  /** Current step index, 0..TUTORIAL_TOTAL_STEPS - 1. */
  private _stepIndex = 0;
  /**
   * Last live host stashed on `start()` so click / key handlers can route
   * through the same seam without the caller threading it on every call.
   * `null` only when the tour is inactive.
   */
  private host: TutorialHost | null = null;

  /** True while the 12-step tour overlay is being shown. */
  get active(): boolean {
    return this._active;
  }

  /** Current step index (0..11). Stable when the tour is inactive. */
  get step(): number {
    return this._stepIndex;
  }

  /** Convenience — the i18n step id of the current step. */
  get stepId(): TutorialStepId | null {
    if (!this._active) return null;
    return TUTORIAL_STEP_IDS[this._stepIndex] ?? null;
  }

  /**
   * Launch the tour from step 1 on a clean board. Snaps the host run back
   * to a fresh wave-0 build phase first so the playfield matches what the
   * step copy describes (no leftover towers / wave in flight).
   */
  start(host: TutorialHost): void {
    this.host = host;
    this._active = true;
    this._stepIndex = 0;
    // The first step ("Welcome to OVERVOLT") describes the title-screen
    // board — make sure that's what the player is looking at, even if they
    // clicked the button mid-run (the title-screen Start Tutorial button
    // hit-test gates this normally, but the host's resetRun is the cheap
    // safety net).
    host.resetRun();
  }

  /**
   * Advance to the next step. On the final step's "next" (or PLAY click)
   * the tour ends and the host profile's `seenFullTutorial` flag flips.
   */
  next(profile: Profile): void {
    if (!this._active) return;
    if (this._stepIndex >= TUTORIAL_TOTAL_STEPS - 1) {
      this.complete(profile);
      return;
    }
    this._stepIndex += 1;
  }

  /** Rewind one step (disabled on step 0). Quiet no-op when at the start. */
  back(): void {
    if (!this._active) return;
    if (this._stepIndex <= 0) return;
    this._stepIndex -= 1;
  }

  /**
   * Skip the tour from wherever the player is. Sets the seen flag (skipping
   * is consent to not see the first-time pulse again) and resets the run
   * to a clean wave-0 board so any towers placed during a scripted step
   * are wiped, matching §4.4's "Skip always restores wave-0 build phase".
   */
  skip(profile: Profile): void {
    if (!this._active) return;
    this.flipFlag(profile);
    this.exit();
  }

  /**
   * Complete the tour — flips the flag, exits cleanly. Public so test hooks
   * can drive a completion without walking every step's auto-advance.
   */
  complete(profile: Profile): void {
    if (!this._active) return;
    this.flipFlag(profile);
    this.exit();
  }

  /** Internal — flips the one-shot flag if not already set and persists. */
  private flipFlag(profile: Profile): void {
    if (profile.seenFullTutorial) return;
    profile.seenFullTutorial = true;
    if (this.host) this.host.persistProfile();
    else saveProfile(profile);
  }

  /** Internal — exit the tour and snap the host board back to wave-0 build. */
  private exit(): void {
    if (this.host) this.host.resetRun();
    this._active = false;
    this._stepIndex = 0;
    this.host = null;
  }

  // --- input ---------------------------------------------------------------

  /**
   * Route a key while the tour is active. Recognised: `escape` (skip),
   * `arrowright` (next), `arrowleft` (back). Anything else is dropped — the
   * tour swallows raw keystrokes to prevent the underlying HUD from acting
   * on them while the overlay is captive.
   */
  handleKey(key: string, profile: Profile): void {
    if (!this._active) return;
    if (key === 'escape') {
      this.skip(profile);
      return;
    }
    if (key === 'arrowright' || key === 'enter' || key === 'space') {
      this.next(profile);
      return;
    }
    if (key === 'arrowleft') {
      this.back();
      return;
    }
  }

  /**
   * Route a pointer click. Returns true when the click was consumed by the
   * tour overlay (in which case `Game.ts` skips its own click routing).
   * Always consumes a click while active — the overlay covers the canvas,
   * so a click anywhere is either an action-button hit or a quiet pass.
   */
  handleClick(x: number, y: number, profile: Profile): boolean {
    if (!this._active) return false;
    const rects = buttonRects(PANEL_DEFAULT_X, PANEL_DEFAULT_Y);
    if (inRect(rects.next, x, y)) {
      this.next(profile);
      return true;
    }
    if (inRect(rects.back, x, y)) {
      this.back();
      return true;
    }
    if (inRect(rects.skip, x, y)) {
      this.skip(profile);
      return true;
    }
    // Click anywhere else on the dim layer is swallowed so the underlying
    // HUD does not act (§4.3: "input is locked to only the elements
    // relevant to the current step"). Defaults to no-op + consume.
    return true;
  }

  // --- render --------------------------------------------------------------

  /**
   * Paint the tour overlay — dim layer, neon text box, step title + body,
   * Back / Skip / Next buttons. Called from `Game.render` after every other
   * UI layer, so it sits on top of the HUD + Profile launcher.
   */
  render(ctx: CanvasRenderingContext2D, time: number): void {
    if (!this._active) return;
    const id = TUTORIAL_STEP_IDS[this._stepIndex];
    if (!id) return;
    const title = t(`tutorial.step.${id}.title`);
    const body = t(`tutorial.step.${id}.body`);
    const eyebrow = t('tutorial.ui.eyebrow', {
      n: this._stepIndex + 1,
      total: TUTORIAL_TOTAL_STEPS,
    });

    // Dim layer — single rgba fill over the whole canvas; the bright
    // neon panel below is what reads "look here", not a cut-out hole
    // (per §4.1's uniform-dim decision).
    ctx.save();
    ctx.fillStyle = 'rgba(5, 8, 16, 0.62)';
    ctx.fillRect(0, 0, CW, CH);

    const px = PANEL_DEFAULT_X;
    const py = PANEL_DEFAULT_Y;
    drawNeonPanel(ctx, px, py, PANEL_W, PANEL_H, time);

    // Eyebrow — small mono-spaced "STEP n / 12 · TITLE".
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.font = `9px ${FONT_MONO}`;
    ctx.fillStyle = withAlpha(C.accent, 0.85);
    ctx.fillText(eyebrow, px + 18, py + 20);

    // Title — bold display font, accent colour.
    ctx.font = `700 18px ${FONT_DISPLAY}`;
    ctx.fillStyle = C.accent;
    ctx.shadowBlur = 8;
    ctx.shadowColor = C.accent;
    ctx.fillText(title, px + 18, py + 42);
    ctx.shadowBlur = 0;

    // Body — wrapped mono text inside the inner card area.
    ctx.font = `11px ${FONT_MONO}`;
    ctx.fillStyle = C.hudText;
    const lines = wrapText(ctx, body, PANEL_W - 36);
    let bodyY = py + 64;
    for (const line of lines) {
      ctx.fillText(line, px + 18, bodyY);
      bodyY += 15;
    }

    // Buttons.
    const rects = buttonRects(px, py);
    const onLast = this._stepIndex === TUTORIAL_TOTAL_STEPS - 1;
    drawActionButton(
      ctx,
      rects.back,
      t('tutorial.ui.back'),
      C.hudMuted,
      this._stepIndex > 0,
    );
    drawActionButton(ctx, rects.skip, t('tutorial.ui.skip'), C.warning, !onLast);
    drawActionButton(
      ctx,
      rects.next,
      onLast ? t('tutorial.ui.play') : t('tutorial.ui.next'),
      C.accent,
      true,
      time,
    );
    ctx.restore();
  }
}

// --- drawing helpers ---------------------------------------------------------

/** True when (x, y) lies inside the rect `r`. */
function inRect(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

/**
 * Render the tour's text-box panel — a neon rounded card with corner
 * brackets, mirroring `onboarding.ts:neonPanel` but lightly inlined here so
 * the tutorial module has no cross-imports into onboarding.ts (the Sprint 25
 * / 53 onboarding plumbing is read-only from this module's perspective).
 */
function drawNeonPanel(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  time: number,
): void {
  const g = ctx.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, 'rgba(14, 22, 42, 0.97)');
  g.addColorStop(1, 'rgba(8, 12, 24, 0.97)');
  ctx.fillStyle = g;
  roundRect(ctx, x, y, w, h, 10);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.55 + 0.22 * Math.sin(time * 2.5));
  ctx.lineWidth = 1.5;
  ctx.shadowBlur = 18;
  ctx.shadowColor = C.accent;
  roundRect(ctx, x, y, w, h, 10);
  ctx.stroke();
  ctx.shadowBlur = 0;
  // Corner brackets — the same crisp diagnostics frame the win / lose
  // overlays use, so the tour reads as part of the HUD-language family.
  ctx.strokeStyle = C.accent;
  ctx.lineWidth = 2.2;
  const k = 14;
  const corner = (px: number, py: number, sx: number, sy: number): void => {
    ctx.beginPath();
    ctx.moveTo(px + sx * k, py);
    ctx.lineTo(px, py);
    ctx.lineTo(px, py + sy * k);
    ctx.stroke();
  };
  corner(x + 6, y + 6, 1, 1);
  corner(x + w - 6, y + 6, -1, 1);
  corner(x + 6, y + h - 6, 1, -1);
  corner(x + w - 6, y + h - 6, -1, -1);
}

/**
 * One action button on the tour panel's bottom row. The `enabled` flag dims
 * disabled buttons (Back on step 0). The accent NEXT / PLAY button accepts
 * an optional `time` so the renderer can pulse it — the eye naturally lands
 * on the primary action.
 */
function drawActionButton(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  label: string,
  color: string,
  enabled: boolean,
  time?: number,
): void {
  ctx.save();
  if (!enabled) ctx.globalAlpha = 0.4;
  const pulse = time !== undefined ? 0.55 + 0.35 * Math.sin(time * 3) : 1;
  ctx.fillStyle = withAlpha(color, 0.18);
  roundRect(ctx, r.x, r.y, r.w, r.h, 5);
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, time !== undefined ? 0.55 + 0.35 * pulse : 0.65);
  ctx.lineWidth = 1.3;
  if (time !== undefined) {
    ctx.shadowBlur = 8 * pulse;
    ctx.shadowColor = color;
  }
  roundRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 5);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.font = `700 11px ${FONT_DISPLAY}`;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 0.5);
  ctx.restore();
}

/**
 * Greedily wrap `text` to lines no wider than `maxWidth` against the
 * current ctx font. Mirrors `hud.ts:wrapLabel` so the tour wraps line-for-
 * line the way the HUD's mode-descriptor pill does.
 */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}
